"use client";

import Image from "next/image";
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { REFERENCE_IMAGE_ACCEPT } from "@/lib/create/referenceImageUpload";
import ChatComposer from "../chat/ChatComposer";
import { type CreateEntryPointProps } from "./entryOptions";
import { useCreateEntry } from "./useCreateEntry";
import { useAnimatedEntryPrompt } from "./useAnimatedEntryPrompt";
import "./create-entry.css";

export { ENTRY_SUGGESTED_PROMPTS } from "./entryOptions";
export type { EntryConversationOptions } from "./entryOptions";

export default function CreateEntryPoint(props: CreateEntryPointProps) {
  const {
    useBottomInset = true,
    entryActionPending = false,
    entryError = null,
  } = props;
  const {
    uploadInputRef,
    handleReferenceUpload,
    uploadPreparing,
    uploadError,
    entryDisabled,
    entryStarting,
    attachmentStatus,
    entryInputValue,
    handleInputChange,
    handleCustomPrompt,
    handleSuggestedPrompt,
    selectedReferences,
    selectedReferenceCount,
    clearSelectedReferences,
  } = useCreateEntry(props);
  const { suggestion, visibleText } = useAnimatedEntryPrompt(
    !entryInputValue && !entryDisabled
  );
  const canUseSuggestion = !entryInputValue && !entryDisabled;
  const acceptSuggestion = () => handleSuggestedPrompt(suggestion);

  return (
    <div data-entry-prompt-widget className="designer-entry">
      <input
        ref={uploadInputRef}
        type="file"
        accept={REFERENCE_IMAGE_ACCEPT}
        multiple
        hidden
        tabIndex={-1}
        data-testid="entry-upload-input"
        onChange={handleReferenceUpload}
      />
      <div className="designer-entry-content">
        <header className="designer-entry-heading">
          <h1>Design your dress.</h1>
          <p>Start with a few words or an inspiration photo.</p>
        </header>
        {uploadPreparing ? (
          <p
            role="status"
            aria-live="polite"
            className="designer-entry-notice text-sm text-muted-foreground"
          >
            Preparing your inspiration photos…
          </p>
        ) : null}
        {entryError ? (
          <p
            role="alert"
            className="designer-entry-notice rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {entryError}
          </p>
        ) : null}
        {uploadError ? (
          <p
            role="alert"
            className="designer-entry-notice rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {uploadError}
          </p>
        ) : null}
        <ChatComposer
          variant="entry"
          useBottomInset={useBottomInset}
          onSendMessage={handleCustomPrompt}
          disabled={entryDisabled}
          isTyping={entryStarting || entryActionPending}
          placeholder="Tell us about your dress…"
          animatedSuggestion={
            canUseSuggestion
              ? {
                  typed: visibleText,
                  remaining: suggestion.message.slice(visibleText.length),
                }
              : undefined
          }
          value={entryInputValue}
          onValueChange={handleInputChange}
          onAcceptSuggestion={canUseSuggestion ? acceptSuggestion : undefined}
          canSendWithoutMessage={selectedReferenceCount > 0}
          suggestions={
            <button
              type="button"
              className="designer-entry-use-idea"
              data-testid="entry-use-idea"
              aria-label={`Use suggested prompt: ${suggestion.message}`}
              aria-hidden={!canUseSuggestion}
              tabIndex={canUseSuggestion ? 0 : -1}
              disabled={!canUseSuggestion}
              onClick={acceptSuggestion}
            >
              <span>Use idea</span>
              <kbd>Tab</kbd>
            </button>
          }
          startAction={
            selectedReferenceCount > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={entryDisabled}
                data-testid="entry-upload-reference"
                onClick={clearSelectedReferences}
                aria-label={
                  selectedReferenceCount === 1
                    ? "Remove inspiration photo"
                    : `Remove ${selectedReferenceCount} inspiration photos`
                }
                aria-busy={uploadPreparing || undefined}
                title="Remove inspiration photos"
                className="relative size-14 touch-manipulation overflow-hidden rounded-2xl p-0"
              >
                <Image
                  src={selectedReferences[0].dataUrl}
                  alt=""
                  fill
                  unoptimized
                  sizes="56px"
                  className="object-cover"
                />
                <span className="absolute right-0.5 top-0.5 grid size-4 place-items-center rounded-full bg-background/90 text-foreground shadow-sm">
                  <X className="size-3" aria-hidden />
                </span>
                {selectedReferenceCount > 1 ? (
                  <span
                    aria-hidden
                    className="absolute bottom-0.5 right-0.5 rounded-full bg-background/90 px-1.5 text-[10px] font-bold text-foreground shadow-sm"
                  >
                    +{selectedReferenceCount - 1}
                  </span>
                ) : null}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={entryDisabled}
                data-testid="entry-upload-reference"
                onClick={() => uploadInputRef.current?.click()}
                aria-label={
                  uploadPreparing
                    ? "Preparing inspiration photos"
                    : "Add up to 3 inspiration photos or sketches"
                }
                aria-busy={uploadPreparing || undefined}
                title="Add inspiration photos or sketches"
                className="designer-entry-upload min-h-11 gap-2 rounded-lg border-0 bg-transparent px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <ImagePlus className="size-4" aria-hidden />
                <span>Add photos</span>
              </Button>
            )
          }
        />
      </div>
      <p role="status" aria-live="polite" className="sr-only">
        {attachmentStatus}
      </p>
    </div>
  );
}
